// Meta's in-app browser injects its own performance logger into our page, and that logger
// throws from its own pagehide/beforeunload hooks as the user navigates away. On Android the
// frames name iabjs://; the iOS variant is injected inline, so its frames carry our page URL
// and only the message identifies it. Not our code, and nothing a user ever sees.
const THIRD_PARTY_IN_APP_BROWSER_MESSAGE = /Error invoking postMessage|window\.webkit\.messageHandlers/

// posthog-js puts the message on $exception_message and on each $exception_list entry's
// .value. There is no $exception_values property on a captured event - the name only
// exists as an internal accumulator inside posthog's own suppression matcher.
const exceptionMessages = event => [
  event.properties?.$exception_message,
  ...(event.properties?.$exception_list || []).map(exception => exception?.value)
].filter(message => typeof message === 'string')

const isThirdPartyInAppBrowserException = (event) => {
  const hasInjectedFrame = (event.properties?.$exception_list || []).some(
    exception => (exception?.stacktrace?.frames || []).some(
      frame => typeof frame?.filename === 'string' && frame.filename.startsWith('iabjs://')
    )
  )
  if (hasInjectedFrame) { return true }

  return exceptionMessages(event).some(message => THIRD_PARTY_IN_APP_BROWSER_MESSAGE.test(message))
}

// Chrome on iOS and the Google app inject their own script into our page, and that script
// overflows its stack. Its frames carry our page URL at line numbers past the end of our
// rendered HTML, or there are no frames at all. Our own code always runs from a bundled .js
// file, so a frame in one keeps the event.
const GOOGLE_IOS_APP_USER_AGENT = /CriOS\/|GSA\//
const STACK_OVERFLOW_MESSAGE = /Maximum call stack size exceeded/
const SCRIPT_FILE = /\.m?js(?:[?#]|$)/

export const isGoogleIosAppStackOverflow = (event, userAgent = navigator.userAgent) => {
  if (!GOOGLE_IOS_APP_USER_AGENT.test(userAgent)) { return false }

  const hasScriptFrame = (event.properties?.$exception_list || []).some(
    exception => (exception?.stacktrace?.frames || []).some(
      frame => typeof frame?.filename === 'string' && SCRIPT_FILE.test(frame.filename)
    )
  )
  if (hasScriptFrame) { return false }

  return exceptionMessages(event).some(message => STACK_OVERFLOW_MESSAGE.test(message))
}

const isInlinePageFrame = (frame, pageOrigin) =>
  typeof frame?.filename === 'string' &&
  frame.filename.startsWith(`${pageOrigin}/`) &&
  !SCRIPT_FILE.test(frame.filename) &&
  frame.lineno === 1

// Meta's iOS in-app browser also throws a WebKit readonly-property TypeError from its inline
// script as the page unloads. Its frames sit on line 1 of our page URL, or in [native code].
// Line 1 of our rendered HTML is the doctype, and our minified bundle is on line 1 of a .js
// file, so any other frame keeps the event.
const META_IN_APP_BROWSER_USER_AGENT = /Instagram|FBAN|FBAV/
const READONLY_PROPERTY_MESSAGE = /Attempting to change value of a readonly property/

export const isMetaIosInAppBrowserReadonlyError = (
  event,
  userAgent = navigator.userAgent,
  pageOrigin = window.location.origin
) => {
  if (!META_IN_APP_BROWSER_USER_AGENT.test(userAgent)) { return false }

  const frames = (event.properties?.$exception_list || []).flatMap(
    exception => exception?.stacktrace?.frames || []
  )
  const isNativeFrame = frame => frame?.filename === '[native code]'

  if (!frames.some(frame => isInlinePageFrame(frame, pageOrigin))) { return false }
  if (!frames.every(frame => isInlinePageFrame(frame, pageOrigin) || isNativeFrame(frame))) { return false }

  return exceptionMessages(event).some(message => READONLY_PROPERTY_MESSAGE.test(message))
}

// Safari reports a blocked request as a bare 'Load failed' TypeError, and content blockers
// often drop the requests that Google Tag Manager and the tags it loads send. Sentry patches
// fetch and wraps callbacks, so its wrapper frames are in our bundle, but only at the two ends
// of the stack. A frame from our bundle at any other position is our own code, and keeps the event.
const TAG_SCRIPT_URL = /^https:\/\/(?:www\.googletagmanager\.com|analytics\.tiktok\.com)\//

export const isBlockedTagScriptLoadFailure = (event, pageOrigin = window.location.origin) => {
  if (!exceptionMessages(event).includes('Load failed')) { return false }

  const stacks = (event.properties?.$exception_list || []).map(
    exception => exception?.stacktrace?.frames || []
  )
  const isTagScriptFrame = frame => typeof frame?.filename === 'string' && TAG_SCRIPT_URL.test(frame.filename)
  const isBundleFrame = frame =>
    typeof frame?.filename === 'string' &&
    frame.filename.startsWith(`${pageOrigin}/`) &&
    SCRIPT_FILE.test(frame.filename)

  if (!stacks.flat().some(isTagScriptFrame)) { return false }

  return stacks.every(frames => frames.every((frame, index) =>
    isTagScriptFrame(frame) ||
    isInlinePageFrame(frame, pageOrigin) ||
    (isBundleFrame(frame) && (index === 0 || index === frames.length - 1))
  ))
}

function mask (str) {
  return str
    ?.replace(/\p{Lu}/gu, 'X')
    .replace(/\p{Ll}/gu, 'x')
    .replace(/[0-9]/g, '0')
}

export function usePostHog (userRandomId) {
  window.addEventListener('CookiebotOnConsentReady', function (e) {
    const { marketing } = window.Cookiebot.consent

    if (marketing) {
      import('posthog-js').then(({ default: posthog }) => {
        posthog.init('phc_e2HKAQn0PEfDgzdMym2I4SOuyP5I1tMWET3Zz2O7Raq', {
          api_host: 'https://eu.i.posthog.com',
          defaults: '2025-05-24',
          persistence: 'localStorage+cookie',
          // Exception autocapture is on implicitly via defaults: '2025-05-24' - this only
          // drops third-party injected-script noise, it does not turn capture off.
          before_send: (event) => {
            if (
              event?.event === '$exception' &&
              (
                isThirdPartyInAppBrowserException(event) ||
                isGoogleIosAppStackOverflow(event) ||
                isMetaIosInAppBrowserReadonlyError(event) ||
                isBlockedTagScriptLoadFailure(event)
              )
            ) {
              return null
            }
            return event
          },
          // Real-user web-vitals monitoring (LCP/CLS/FCP/INP) → $web_vitals events.
          // Same marketing-consent gate as all other PostHog capture.
          capture_performance: { web_vitals: true },
          session_recording: {
            maskAllInputs: true,
            maskInputFn (text, element) {
              const inputType = element.getAttribute('type')
              const duskType = element.getAttribute('dusk')

              if (inputType === 'password' || duskType === 'password') {
                return '•'.repeat(text.length)
              }

              if (inputType === 'email' || duskType === 'email') {
                return mask(text)
              }

              if (element.getAttribute('autocomplete')) {
                return mask(text)
              }

              if (element.classList.contains('pii')) {
                return mask(text)
              }

              return text
            },
            maskTextSelector: '.pii',
            maskTextFn (text) {
              return mask(text)
            }
          }
        })

        if (userRandomId) {
          posthog.identify(userRandomId)
        }
      })
    }
  })
}
